import React, { useState, useEffect, useCallback } from 'react';
import './StatsPage.css';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const StatsPage = () => {
    const [confusionMatrix, setConfusionMatrix] = useState(null);
    const [intersections, setIntersections] = useState(null);
    const [unions, setUnions] = useState(null);
    const [userdata, setUserdata] = useState(null);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

    const authorizedUsers = Object.freeze([
        '108129870778035595919',
        '114439477008986381098',
        '102906129011434823565',
        '108247700055597509497'
    ]);

    // Fetch user data securely
    const getUser = useCallback(async () => {
        let mounted = true;
        try {
            const response = await axios.get(`${BACKEND_URL}/ttc/login/userdata`, {
                withCredentials: true,
                timeout: 8000
            });
            if (!mounted) return;

            const user = response?.data?.user;
            if (!user || !authorizedUsers.includes(user.googleId)) {
                navigate('/login'); // Not authorized
                return;
            }

            setUserdata(user);
        } catch (err) {
            console.error('Error fetching user data:', err);
            navigate('/login'); // Redirect on error
        }
        return () => { mounted = false; };
    }, [BACKEND_URL, navigate, authorizedUsers]);

    useEffect(() => {
        getUser();
    }, [getUser]);

    const fetchData = useCallback(async (url, setter, defaultMessage) => {
        let mounted = true;
        try {
            const response = await axios.get(`${BACKEND_URL}${url}`, {
                withCredentials: true,
                timeout: 8000
            });
            if (!mounted) return;
            setter(response.data || {});
        } catch (err) {
            console.error(`Error fetching ${defaultMessage}:`, err);
            setter({});
        }
        return () => { mounted = false; };
    }, [BACKEND_URL]);

    if (!userdata) return <div>Loading...</div>;
    if (error) return <div>{error}</div>;

    const safeMap = (data, type) =>
        Array.isArray(data?.[type]) && data[type].length > 0
            ? data[type].map((recipeId, index) => (
                  <span key={recipeId}>
                      <a
                          href={`/ttc/recipe/${encodeURIComponent(recipeId)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                      >
                          {recipeId}
                      </a>
                      {index < data[type].length - 1 && ', '}
                  </span>
              ))
            : `No ${type === 'intersections' ? 'intersection' : 'union'} found`;

    return (
        <div className="stats-container">
            <h1>Statistics</h1>
            <p>Click the button to get the latest confusion matrix data.</p>
            <button onClick={() => fetchData('/ttc/api/fetch-confusion-matrix', setConfusionMatrix, 'confusion matrix')}>Run</button>
            <button onClick={() => fetchData('/ttc/api/fetch-intersections', setIntersections, 'intersections')}>Fetch Intersections</button>
            <button onClick={() => fetchData('/ttc/api/fetch-unions', setUnions, 'unions')}>Fetch Unions</button>

            {confusionMatrix && (
                <div>
                    <h2>Confusion Matrix</h2>
                    <table className="matrix-table">
                        <thead>
                            <tr>
                                <th>Actual / Predicted</th>
                                <th>Fake</th>
                                <th>Real</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Fake</td>
                                <td>{confusionMatrix.FF || 0}</td>
                                <td>{confusionMatrix.FR || 0}</td>
                            </tr>
                            <tr>
                                <td>Real</td>
                                <td>{confusionMatrix.RF || 0}</td>
                                <td>{confusionMatrix.RR || 0}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}

            {intersections && (
                <div>
                    <h2>Intersections</h2>
                    <table className="matrix-table">
                        <thead>
                            <tr>
                                <th>Type</th>
                                <th>Values</th>
                            </tr>
                        </thead>
                        <tbody>
                            {['FF', 'RF', 'FR', 'RR'].map(type => (
                                <tr key={type}>
                                    <td>{type}</td>
                                    <td>{safeMap(intersections, type)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {unions && (
                <div>
                    <h2>Unions</h2>
                    <table className="matrix-table">
                        <thead>
                            <tr>
                                <th>Type</th>
                                <th>Values</th>
                            </tr>
                        </thead>
                        <tbody>
                            {['FF', 'RF', 'FR', 'RR'].map(type => (
                                <tr key={type}>
                                    <td>{type}</td>
                                    <td>{safeMap(unions, type)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default StatsPage;
